import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Button, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Divider,
} from '@mui/material';
import {
  EventNote as EventNoteIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarIcon,
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  Group as GroupIcon,
  School as SchoolIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import Loading from '../common/Loading';
import { studentAPI } from '../../services/api';
import { DocumentSecurity } from '../../services/documentSecurity';

const DAY_FR = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];

const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  // Parse date parts directly to avoid UTC-vs-local timezone shift
  // new Date("2026-06-04") is UTC midnight → shifts one day back in UTC-X timezones
  const [y, m, dd] = String(dateStr).substring(0, 10).split('-').map(Number);
  if (!y || !m || !dd) return String(dateStr);
  const d = new Date(y, m - 1, dd); // local constructor — no timezone conversion
  return `${DAY_FR[d.getDay()]} ${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
};

const formatTime = (t) => t ? t.substring(0, 5) : '-';

// Strip anything from "(" onwards — e.g. "1(JL AP43 05)" → "1"
const cleanGroup = (g) => g ? g.replace(/\s*\(.*/, '').trim() : '-';

// Arabic module name if available, otherwise French, otherwise code
const moduleName = (ex) => {
  if (ex.module_name_arb && ex.module_name_arb.trim()) return ex.module_name_arb.trim();
  if (ex.module_name_fr  && ex.module_name_fr.trim())  return ex.module_name_fr.trim();
  return ex.module_code || '-';
};

const SESSION_TYPE_LABELS = {
  NORMALE:    'Session Printemps Normale',
  RATTRAPAGE: 'Session Printemps Rattrapage',
  AUTOMNE:    "Session Rattrapage d'Automne",
  PRINTEMPS:  'Session de Printemps',
};

const SEM_COLORS = {
  S1: '#2563eb', S2: '#d97706', S3: '#16a34a',
  S4: '#7c3aed', S5: '#dc2626', S6: '#0891b2',
};

const ExamConvocation = ({ menuConfig = {} }) => {
  const { user } = useAuth();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [generating, setGenerating] = useState(false);

  // menuConfig['convocation.pdf'] is true by default; only false when admin explicitly disables it
  const pdfEnabled = menuConfig['convocation.pdf'] !== false;

  useEffect(() => {
    studentAPI.getExamConvocation()
      .then(res => setData(res))
      .catch(e => setError(e.response?.data?.error || 'Erreur de chargement'))
      .finally(() => setLoading(false));
  }, []);

  const generatePDF = async () => {
    setGenerating(true);
    try {
      const jsPDF = (await import('jspdf')).default;
      await import('jspdf-autotable');

      const doc = new jsPDF('p', 'mm', 'a4');
      const W = doc.internal.pageSize.getWidth();
      const H = doc.internal.pageSize.getHeight();

      // Amiri font for Arabic
      try {
        const fontRes = await fetch('/fonts/Amiri-Regular.ttf');
        if (fontRes.ok) {
          const blob = await fontRes.blob();
          const b64  = await new Promise((res, rej) => {
            const r = new FileReader();
            r.onloadend = () => res(r.result.split(',')[1]);
            r.onerror   = rej;
            r.readAsDataURL(blob);
          });
          doc.addFileToVFS('Amiri-Regular.ttf', b64);
          doc.addFont('Amiri-Regular.ttf', 'Amiri', 'normal');
          doc.addFont('Amiri-Regular.ttf', 'Amiri', 'bold');
        }
      } catch { /* fallback */ }

      // Logos
      const fetchImgB64 = async (url) => {
        const blob = await fetch(url).then(r => r.blob());
        return new Promise((res) => {
          const reader = new FileReader();
          reader.onloadend = () => res(reader.result.split(',')[1]);
          reader.readAsDataURL(blob);
        });
      };
      let logo1 = null, logo2 = null;
      try { logo1 = await fetchImgB64('/logo1.png'); } catch { /* optional */ }
      try { logo2 = await fetchImgB64('/logo2.png'); } catch { /* optional */ }

      doc.setFont('Amiri', 'normal');
      const primary   = [44, 62, 80];
      const textColor = [33, 37, 41];

      // ── Header: logos + centred text on white background ──
      if (logo1) doc.addImage(logo1, 'PNG', 5, 5, 42, 35);
      if (logo2) doc.addImage(logo2, 'PNG', W - 40, 5, 28, 28);
      doc.setTextColor(...primary);
      doc.setFontSize(11); doc.setFont('Amiri', 'bold');
      doc.text('FACULTÉ DES SCIENCES JURIDIQUES ET POLITIQUES', W / 2, 13, { align: 'center' });
      doc.setFont('Amiri', 'normal'); doc.setFontSize(9.5);
      doc.text('UNIVERSITÉ HASSAN 1ER - SETTAT', W / 2, 20, { align: 'center' });
      doc.text('جامعة الحسن الأول - سطات', W / 2, 27, { align: 'center' });
      doc.setFontSize(9);
      doc.text('كلية العلوم القانونية والسياسية', W / 2, 34, { align: 'center' });
      // Double separator line
      doc.setDrawColor(...primary);
      doc.setLineWidth(0.6);
      doc.line(10, 38, W - 10, 38);
      doc.setLineWidth(0.2);
      doc.line(10, 39.5, W - 10, 39.5);

      // ── Academic year & session ───────────────────────────────
      const session    = data?.session;
      const acadYear   = session?.academic_year || new Date().getFullYear();
      const sessionLabel = SESSION_TYPE_LABELS[session?.session_type] || session?.name || 'Session';

      doc.setTextColor(...textColor);
      doc.setFontSize(10); doc.setFont('Amiri', 'normal');
      doc.text(`Année Universitaire ${parseInt(acadYear) - 1}-${acadYear}`, W / 2, 47, { align: 'center' });
      doc.text(sessionLabel, W / 2, 54, { align: 'center' });

      // ── Document title ────────────────────────────────────────
      doc.setFontSize(17); doc.setFont('Amiri', 'bold');
      doc.text("Convocation d'examen", W / 2, 65, { align: 'center' });
      doc.setFontSize(12); doc.setFont('Amiri', 'normal');
      doc.text('استدعاء اجتياز الامتحان', W / 2, 72, { align: 'center' });

      // ── Student info ──────────────────────────────────────────
      const st     = data?.student || {};
      const nom    = st.lib_nom_pat_ind || user?.nom_complet?.split(' ')[0] || '-';
      const prenom = st.lib_pr1_ind || '-';
      const apogee = st.cod_etu || user?.cod_etu || '-';
      const cne    = st.cod_nne_ind || '-';
      const cin    = st.cin_ind || '-';
      const libetp = st.lib_etp || '-';

      doc.autoTable({
        body: [
          ['Nom:',       nom,    'CNE:',    cne],
          ['Prénom:',    prenom, 'CIN:',    cin],
          ['N° Apogée:', { content: apogee, colSpan: 3, fontStyle: 'bold', fontSize: 13 }],
        ],
        startY: 80,
        theme: 'plain',
        styles: { fontSize: 11, cellPadding: 2.5, font: 'Amiri' },
        columnStyles: {
          0: { fontStyle: 'bold', cellWidth: 28 },
          1: { fontStyle: 'bold', cellWidth: 62, fontSize: 13 },
          2: { fontStyle: 'bold', cellWidth: 22 },
          3: { fontStyle: 'bold', cellWidth: 78, fontSize: 13 },
        },
      });
      // ── thin line under student info ──────────────────────────
      doc.setDrawColor(200, 200, 200);
      doc.setLineWidth(0.3);
      doc.line(10, doc.lastAutoTable.finalY + 1, W - 10, doc.lastAutoTable.finalY + 1);

      // ── Exams table: N° | Sem | Date | Horaire | Matière | Amphi | Gr | Prof ──
      const exams = data?.exams || [];
      const rows  = exams.map((ex, i) => [
        ex.student_order != null ? String(ex.student_order) : String(i + 1),
        formatDate(ex.exam_date),
        formatTime(ex.start_time),
        moduleName(ex),
        ex.location || '-',
        cleanGroup(ex.group_name),
        ex.professor_name || '-',
      ]);

      doc.autoTable({
        startY: doc.lastAutoTable.finalY + 5,
        head: [['N°', 'Date', 'Horaire', 'Matière', 'Amphi', 'Gr', 'Prof']],
        body: rows,
        theme: 'grid',
        styles: {
          font: 'Amiri', fontSize: 9, cellPadding: 2,
          textColor, lineColor: [200, 200, 200], lineWidth: 0.2,
        },
        headStyles: { fillColor: primary, textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 9 },
        columnStyles: {
          0: { halign: 'center', cellWidth: 14 },
          1: { halign: 'center', cellWidth: 28 },
          2: { halign: 'center', cellWidth: 22 },
          3: { cellWidth: 60 },
          4: { halign: 'center', cellWidth: 20 },
          5: { halign: 'center', cellWidth: 10 },
          6: { cellWidth: 36 },
        },
      });

      // ── Exam conditions (Arabic, right-aligned) ───────────────
      const conditionsY = doc.lastAutoTable.finalY + 8;
      doc.setFontSize(9); doc.setFont('Amiri', 'bold'); doc.setTextColor(...textColor);
      doc.text('شروط خاصة باجتياز الامتحان', W - 15, conditionsY, { align: 'right' });
      doc.setFontSize(8); doc.setFont('Amiri', 'normal');
      let cy = conditionsY + 6;
      [
        'يجب على الطالب(ة) الحضور إلى مدرج الإمتحان قبل التوقيت المحدد.',
        'عدم اجتياز الطالب(ة) الإمتحان في مدرج الإمتحان و المجموعة المشار إليهما في استدعاء الإمتحان يجعله في حكم الغائب.',
        'ضرورة إدلاء الطالب(ة) خلال الامتحان ببطاقة الطالب(ة) و باستدعاء اجتياز الإمتحان نسخة ورقية.',
        'يمنع منعا كليا حيازة أو استعمال الهاتف المحمول أو وثائق أو مطبوعات غير مرخص بها داخل مدرج الامتحان.',
      ].forEach(line => {
        doc.text(line, W - 15, cy, { align: 'right', maxWidth: W - 50 });
        cy += 6;
      });

      // ── QR code & signature ───────────────────────────────────
      const docData = {
        studentId: apogee,
        semester:  `convocation-${session?.id || 0}`,
        timestamp: new Date().toISOString(),
        modules:   exams.map(e => e.id),
      };
      const { signature: sig, qrCodeDataUrl: qr } = await DocumentSecurity.signDocument(docData);

      // ── Footer ────────────────────────────────────────────────
      const genDate = new Date().toLocaleString('fr-FR');
      doc.setFontSize(7.5); doc.setFont('Amiri', 'normal'); doc.setTextColor(120);
      doc.text(`حرر بسطات في : ${genDate}`, 15, H - 22);
      doc.text(`ID: CONV-${apogee}-${session?.id || 0}-${Date.now()} | Sig: ${sig}`, 15, H - 16);
      doc.text(`Généré le ${genDate}`, W / 2, H - 8, { align: 'center' });

      // QR code — placed above footer, bottom-right, clear of text
      if (qr) {
        try { doc.addImage(qr.split(',')[1], 'PNG', W - 42, H - 56, 32, 32); } catch { /* skip */ }
      }

      // Log the download so admins can track it
      studentAPI.logDocumentDownload(
        'convocation',
        `session-${session?.id || 0}-${sessionLabel}`
      ).catch(() => {});

      doc.save(`Convocation_${apogee}_${session?.id || 0}.pdf`);
    } catch (e) {
      console.error('PDF generation error:', e);
      setError('Erreur lors de la génération du PDF.');
    } finally {
      setGenerating(false);
    }
  };

  if (loading) return <Loading message="Chargement de la convocation..." />;

  const exams   = data?.exams || [];
  const session = data?.session;

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>

      {/* Page header */}
      <Box sx={{
        background: 'linear-gradient(135deg, #1a2a3a 0%, #2c3e50 60%, #3d566e 100%)',
        borderRadius: 4, p: { xs: 2.5, md: 3.5 }, mb: 3, color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2,
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{
            width: 52, height: 52, borderRadius: 3, bgcolor: 'rgba(255,255,255,0.15)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}>
            <EventNoteIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} lineHeight={1.2}>استدعاء الامتحان</Typography>
            <Typography variant="body2" sx={{ opacity: 0.75, mt: 0.3 }}>
              Convocation d'examen — programme des examens
            </Typography>
            {session && (
              <Typography variant="caption" sx={{ opacity: 0.6, display: 'block', mt: 0.3 }}>
                {SESSION_TYPE_LABELS[session.session_type] || session.name}
                {' · '}{parseInt(session.academic_year) - 1}-{session.academic_year}
              </Typography>
            )}
          </Box>
        </Box>

      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {!pdfEnabled && exams.length > 0 && (
        <Alert severity="info" sx={{ mb: 3, borderRadius: 2, direction: 'rtl', textAlign: 'right' }}>
          التحميل متوقف مؤقتاً — يرجى المراجعة لاحقاً
        </Alert>
      )}

      {/* No active session */}
      {!session && (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 4, border: '1.5px dashed #e0e0e0' }}>
          <EventNoteIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" fontWeight={600} gutterBottom>
            لا توجد دورة امتحانية نشطة
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Aucune session d'examen active pour le moment.
          </Typography>
        </Paper>
      )}

      {/* Active session but no exams assigned */}
      {session && exams.length === 0 && (
        <Paper elevation={0} sx={{ p: 6, textAlign: 'center', borderRadius: 4, border: '1.5px dashed #e0e0e0' }}>
          <CalendarIcon sx={{ fontSize: 56, color: '#cbd5e1', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" fontWeight={600} gutterBottom>
            لا توجد امتحانات مجدولة
          </Typography>
          <Typography variant="body2" color="text.disabled">
            Aucun examen planifié pour vous dans cette session.
          </Typography>
          <Chip
            label={SESSION_TYPE_LABELS[session.session_type] || session.name}
            sx={{ mt: 2, fontWeight: 600 }}
            color="primary"
            variant="outlined"
          />
        </Paper>
      )}

      {/* Exam schedule table */}
      {session && exams.length > 0 && (
        <>
        <Paper elevation={0} sx={{ borderRadius: 3, border: '1.5px solid #e2e8f0', overflow: 'hidden' }}>

          {/* Session banner */}
          <Box sx={{
            px: 3, py: 2,
            background: 'linear-gradient(90deg, #2c3e50 0%, #34495e 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1,
          }}>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'white' }}>
                {SESSION_TYPE_LABELS[session.session_type] || session.name}
              </Typography>
              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)' }}>
                Année Universitaire {parseInt(session.academic_year) - 1}-{session.academic_year}
              </Typography>
            </Box>
            <Chip
              label={`${exams.length} examen${exams.length > 1 ? 's' : ''}`}
              sx={{ bgcolor: 'rgba(255,255,255,0.15)', color: 'white', fontWeight: 700, fontSize: '0.78rem' }}
            />
          </Box>

          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 700 }}>
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', py: 1.5, width: 36 }}>N°</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><CalendarIcon sx={{ fontSize: 13 }} /> Date</Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><TimeIcon sx={{ fontSize: 13 }} /> Horaire</Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', py: 1.5 }}>Matière</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><LocationIcon sx={{ fontSize: 13 }} /> Amphi</Box>
                  </TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'center' }}><GroupIcon sx={{ fontSize: 13 }} /> Gr</Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700, color: '#475569', fontSize: '0.75rem', py: 1.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><PersonIcon sx={{ fontSize: 13 }} /> Prof</Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exams.map((ex, i) => {
                  return (
                    <TableRow
                      key={ex.id}
                      sx={{ '&:nth-of-type(even)': { bgcolor: '#fafafa' }, '&:hover': { bgcolor: '#f0f4ff' } }}
                    >
                      {/* N° — matches emargement order in exam room */}
                      <TableCell align="center" sx={{ py: 1.5 }}>
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          width: 26, height: 26, borderRadius: '50%',
                          bgcolor: '#2c3e50', color: 'white', fontWeight: 700, fontSize: '0.75rem',
                        }}>
                          {ex.student_order != null ? ex.student_order : i + 1}
                        </Box>
                      </TableCell>

                      {/* Date */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b', fontSize: '0.82rem' }}>
                          {formatDate(ex.exam_date)}
                        </Typography>
                      </TableCell>

                      {/* Horaire */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Chip
                          label={formatTime(ex.start_time)}
                          size="small"
                          sx={{ fontWeight: 700, bgcolor: '#eff6ff', color: '#1d4ed8', fontSize: '0.78rem', height: 22 }}
                        />
                      </TableCell>

                      {/* Matière */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" fontWeight={600} sx={{ color: '#1e293b', lineHeight: 1.3 }}>
                          {moduleName(ex)}
                        </Typography>
                        {ex.module_code && (
                          <Typography variant="caption" sx={{ color: '#94a3b8', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                            {ex.module_code}
                          </Typography>
                        )}
                      </TableCell>

                      {/* Amphi */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Chip
                          label={ex.location || '-'}
                          size="small"
                          sx={{ fontWeight: 600, bgcolor: '#f0fdf4', color: '#15803d', fontSize: '0.78rem', height: 22 }}
                        />
                      </TableCell>

                      {/* Gr — clean number only */}
                      <TableCell align="center" sx={{ py: 1.5 }}>
                        <Box sx={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                          minWidth: 28, height: 24, borderRadius: 1, px: 1,
                          bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: '0.78rem',
                        }}>
                          {cleanGroup(ex.group_name)}
                        </Box>
                      </TableCell>

                      {/* Prof */}
                      <TableCell sx={{ py: 1.5 }}>
                        <Typography variant="body2" sx={{ color: '#475569', fontSize: '0.82rem' }}>
                          {ex.professor_name || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Conditions footer */}
          <Box sx={{ px: 3, py: 2.5, bgcolor: '#f8fafc', borderTop: '1px solid #e2e8f0' }}>
            <Typography variant="caption" fontWeight={700} sx={{ color: '#475569', display: 'block', mb: 1, textAlign: 'right' }}>
              شروط خاصة باجتياز الامتحان
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            {[
              'يجب على الطالب(ة) الحضور إلى مدرج الإمتحان قبل التوقيت المحدد.',
              'عدم اجتياز الطالب(ة) الإمتحان في مدرج الإمتحان و المجموعة المشار إليهما في استدعاء الإمتحان يجعله في حكم الغائب.',
              'ضرورة إدلاء الطالب(ة) خلال الامتحان ببطاقة الطالب(ة) و باستدعاء اجتياز الإمتحان نسخة ورقية.',
              'يمنع منعا كليا حيازة أو استعمال الهاتف المحمول أو وثائق أو مطبوعات غير مرخص بها داخل مدرج الامتحان.',
            ].map((c, i) => (
              <Typography
                key={i}
                variant="caption"
                sx={{ display: 'block', textAlign: 'right', direction: 'rtl', color: '#64748b', lineHeight: 1.9, fontSize: '0.78rem' }}
              >
                {c}
              </Typography>
            ))}
          </Box>
        </Paper>

        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mt: 3, mb: 1 }}>
          <Button
            variant="contained"
            size="large"
            startIcon={generating ? <CircularProgress size={18} color="inherit" /> : <DownloadIcon />}
            onClick={pdfEnabled ? generatePDF : undefined}
            disabled={!pdfEnabled || generating}
            sx={{
              px: 5, py: 1.5, borderRadius: 3, fontWeight: 700, fontSize: '1rem',
              bgcolor: '#2c3e50',
              '&:hover': { bgcolor: '#34495e' },
              '&:disabled': { bgcolor: '#94a3b8', cursor: 'not-allowed' },
            }}
          >
            {generating ? 'Génération...' : 'تحميل الاستدعاء'}
          </Button>
          {!pdfEnabled && (
            <Typography variant="body2" sx={{ mt: 1.5, color: '#b45309', fontWeight: 600, textAlign: 'center' }}>
              تحميل الاستدعاء غير متاح حاليا، يرجى المحاولة لاحقا
            </Typography>
          )}
        </Box>
        </>
      )}
    </Box>
  );
};

export default ExamConvocation;
